import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getArrangementData, submitPaymentArrangement, getArrangementStatus } from '../services/api';
import { useSession } from '../contexts/SessionContext';
import './PaymentArrangementForm.css';

const STEPS = ['Amount', 'Frequency', 'Bank Details', 'Confirm', 'Done'];

const ACCOUNT_TYPES = [
  { value: 'cheque', label: 'Cheque Account' },
  { value: 'savings', label: 'Savings Account' },
  { value: 'transmission', label: 'Transmission Account' },
];

/**
 * Check if current SAST time is in the DebiCheck blackout window (8pm–3am).
 */
function isDebiCheckBlackout() {
  const now = new Date();
  const sast = new Date(now.toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' }));
  const hour = sast.getHours();
  return hour >= 20 || hour < 3;
}

/**
 * Get tomorrow's date as YYYY-MM-DD string.
 */
function getTomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

/**
 * Get ordinal suffix for a day number.
 */
function ordinal(day) {
  if (day === 1 || day === 21 || day === 31) return `${day}st`;
  if (day === 2 || day === 22) return `${day}nd`;
  if (day === 3 || day === 23) return `${day}rd`;
  return `${day}th`;
}

/**
 * Format a number as South African Rand.
 */
function formatCurrency(amount) {
  if (amount == null) return 'R0.00';
  return `R${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Mask an account number showing only last 4 digits.
 */
function maskAccountNumber(num) {
  if (!num || num.length <= 4) return num || '';
  return '*'.repeat(num.length - 4) + num.slice(-4);
}

/**
 * Get the day-of-week name from a date string.
 */
function getDayName(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-ZA', { weekday: 'long' });
}

/**
 * DebiCheck payment arrangement form — multi-step flow.
 *
 * Steps:
 * 1. Amount selection (cards)
 * 2. Frequency & pay date
 * 3. Bank details
 * 4. Confirm & submit
 * 5. Awaiting approval (success)
 */
const MANDATE_POLL_INTERVAL_MS = 15_000; // 15 seconds
const MANDATE_POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/** Terminal mandate statuses that stop polling */
const TERMINAL_STATUSES = ['authorised', 'rejected', 'canceled', 'suspended'];

export default function PaymentArrangementForm({ email, phone, onEmailChange, onPhoneChange, loanApplicationId, onDone }) {
  const { sessionToken } = useSession();

  // ── Data from backend ──
  const [arrangementData, setArrangementData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // ── Multi-step state ──
  const [step, setStep] = useState(1);

  // ── Form fields ──
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [nextPayDate, setNextPayDate] = useState('');
  const [bankId, setBankId] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [consent, setConsent] = useState(false);

  // ── Submission state ──
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // ── Mandate status polling ──
  const [mandateStatus, setMandateStatus] = useState(null); // null | 'pending' | 'no_response' | 'authorised' | 'rejected' | 'canceled' | 'suspended'
  const [mandateMessage, setMandateMessage] = useState(null);
  const mandatePollRef = useRef(null);
  const mandateTimeoutRef = useRef(null);

  const stopMandatePolling = useCallback(() => {
    if (mandatePollRef.current) {
      clearInterval(mandatePollRef.current);
      mandatePollRef.current = null;
    }
    if (mandateTimeoutRef.current) {
      clearTimeout(mandateTimeoutRef.current);
      mandateTimeoutRef.current = null;
    }
  }, []);

  // Start polling when step 5 is reached with a successful submission
  useEffect(() => {
    if (step !== 5 || !submitResult?.success || !sessionToken) return;

    const pollStatus = async () => {
      try {
        const result = await getArrangementStatus(sessionToken);
        if (result.status) {
          setMandateStatus(result.status);
          if (result.message) setMandateMessage(result.message);

          if (TERMINAL_STATUSES.includes(result.status)) {
            stopMandatePolling();
          }
        }
      } catch {
        // Non-critical: keep polling on transient errors
      }
    };

    // Initial poll
    pollStatus();

    // Set up interval
    mandatePollRef.current = setInterval(pollStatus, MANDATE_POLL_INTERVAL_MS);

    // Set up timeout to stop polling after 5 minutes
    mandateTimeoutRef.current = setTimeout(() => {
      stopMandatePolling();
    }, MANDATE_POLL_TIMEOUT_MS);

    return stopMandatePolling;
  }, [step, submitResult, sessionToken, stopMandatePolling]);

  // ── Blackout ──
  const [blackout, setBlackout] = useState(isDebiCheckBlackout());

  // Re-check blackout every 60s
  useEffect(() => {
    const interval = setInterval(() => setBlackout(isDebiCheckBlackout()), 60000);
    return () => clearInterval(interval);
  }, []);

  // ── Fetch arrangement data ──
  useEffect(() => {
    if (!loanApplicationId) return;
    let cancelled = false;
    setDataLoading(true);
    setDataError(null);

    getArrangementData(loanApplicationId)
      .then((data) => {
        if (cancelled) return;
        setArrangementData(data);
        // Pre-populate contact info
        if (data.email && !email) onEmailChange(data.email);
        if (data.phone && !phone) onPhoneChange(data.phone);
        setContactEmail(data.email || email || '');
        setContactPhone(data.phone || phone || '');
        // Pre-populate bank if available
        if (data.prePopulatedBank) {
          setBankId(String(data.prePopulatedBank.bankId));
          setAccountNumber(data.prePopulatedBank.accountNumber || '');
          setAccountType(data.prePopulatedBank.accountType || '');
        }
      })
      .catch((err) => {
        if (!cancelled) setDataError(err.message || 'Unable to load arrangement details.');
      })
      .finally(() => {
        if (!cancelled) setDataLoading(false);
      });

    return () => { cancelled = true; };
  }, [loanApplicationId]);
  // Sync contact fields from parent
  useEffect(() => {
    if (email && !contactEmail) setContactEmail(email);
    if (phone && !contactPhone) setContactPhone(phone);
  }, [email, phone]);
  // ── Derived values ──
  const selectedBankInfo = useMemo(() => {
    if (!arrangementData?.availableBanks || !bankId) return null;
    return arrangementData.availableBanks.find(b => String(b.id) === bankId);
  }, [arrangementData, bankId]);

  const frequencyLabel = useMemo(() => {
    if (!arrangementData?.availableFrequencies || !selectedFrequency) return '';
    const freq = arrangementData.availableFrequencies.find(f => f.value === selectedFrequency);
    return freq?.label || '';
  }, [arrangementData, selectedFrequency]);

  const derivedDebitInfo = useMemo(() => {
    if (!nextPayDate) return '';
    const d = new Date(nextPayDate);
    const day = d.getDate();
    const dayName = getDayName(nextPayDate);

    if (selectedFrequency === 8) {
      return `Collections will be on the ${ordinal(day)} of each month`;
    } else if (selectedFrequency === 4) {
      return `Collections will be every second ${dayName}`;
    } else if (selectedFrequency === 2) {
      return `Collections will be every ${dayName}`;
    }
    return '';
  }, [nextPayDate, selectedFrequency]);

  // ── Step validation ──
  const canProceedStep1 = selectedAmount !== null;
  const canProceedStep2 = selectedFrequency !== null && nextPayDate !== '';
  const canProceedStep3 = bankId !== '' && accountNumber.trim() !== '' && accountType !== '';
  const canSubmit = consent && !submitting;

  // ── Navigation ──
  const goNext = () => {
    if (step < 4) setStep(step + 1);
  };
  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        loanApplicationId,
        amount: selectedAmount,
        frequency: selectedFrequency,
        nextPayDate,
        bankId: bankId ? parseInt(bankId, 10) : null,
        accountNumber: accountNumber.trim(),
        accountType,
        email: contactEmail || undefined,
        phone: contactPhone || undefined,
      };

      const result = await submitPaymentArrangement(payload);
      setSubmitResult(result);
      setStep(5);
    } catch (err) {
      const errorData = err.data || {};
      setSubmitError({
        message: errorData.message || err.message || 'Something went wrong. Please try again.',
        errors: errorData.errors || [],
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Retry data load ──
  const retryLoad = () => {
    setDataError(null);
    setDataLoading(true);
    getArrangementData(loanApplicationId)
      .then(setArrangementData)
      .catch((err) => setDataError(err.message || 'Unable to load arrangement details.'))
      .finally(() => setDataLoading(false));
  };

  // ── Handle "Done" ──
  const handleDone = () => {
    if (onDone) onDone();
  };

  // ── Render: Step 5 — Awaiting Approval / Mandate Status ──
  if (step === 5 && submitResult?.success) {
    const isTerminal = mandateStatus && TERMINAL_STATUSES.includes(mandateStatus);
    const isAuthorised = mandateStatus === 'authorised';
    const isRejected = mandateStatus === 'rejected';
    const isCanceledOrSuspended = mandateStatus === 'canceled' || mandateStatus === 'suspended';
    const isPending = !mandateStatus || mandateStatus === 'pending' || mandateStatus === 'no_response';

    return (
      <div className="section" data-testid="section-arrange">
        <div className="arrange-success" data-testid="arrange-success">
          {/* Icon changes based on mandate status */}
          {isAuthorised ? (
            <div className="arrange-success__icon arrange-success__icon--approved" data-testid="mandate-approved-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
          ) : isRejected || isCanceledOrSuspended ? (
            <div className="arrange-success__icon arrange-success__icon--warning" data-testid="mandate-warning-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
          ) : (
            <div className="arrange-success__icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
          )}

          {/* Title changes based on mandate status */}
          <h3 className="arrange-success__title">
            {isAuthorised ? 'DebiCheck Mandate Approved' :
             isRejected ? 'DebiCheck Mandate Declined' :
             isCanceledOrSuspended ? (mandateStatus === 'canceled' ? 'DebiCheck Mandate Cancelled' : 'DebiCheck Mandate Suspended') :
             'DebiCheck Mandate Sent'}
          </h3>

          {submitResult.referenceNumber && (
            <p className="arrange-success__ref">
              Reference: <strong>{submitResult.referenceNumber}</strong>
            </p>
          )}

          {/* Message changes based on mandate status */}
          <div className="arrange-success__message">
            {isAuthorised && (
              <div className="notice notice--success" data-testid="mandate-status-authorised">
                <p>DebiCheck mandate approved! Collections will begin on your next debit day.</p>
              </div>
            )}
            {isRejected && (
              <div className="notice notice--warning" data-testid="mandate-status-rejected">
                <p>Your mandate was declined. Please contact us.</p>
              </div>
            )}
            {mandateStatus === 'canceled' && (
              <div className="notice notice--warning" data-testid="mandate-status-canceled">
                <p>Your DebiCheck mandate has been cancelled. Please contact us to arrange an alternative payment method.</p>
              </div>
            )}
            {mandateStatus === 'suspended' && (
              <div className="notice notice--warning" data-testid="mandate-status-suspended">
                <p>Your DebiCheck mandate has been suspended. Please contact us for more information.</p>
              </div>
            )}
            {isPending && (
              <>
                <p>Please open your banking app and approve the DebiCheck mandate.</p>
                <div className="arrange-mandate-polling" data-testid="mandate-polling-indicator">
                  <div className="spinner spinner--small" />
                  <span className="arrange-mandate-polling__text">Waiting for approval...</span>
                </div>
                <p className="arrange-success__note">This may take a few minutes to appear in your app.</p>
              </>
            )}
          </div>

          <div className="arrange-success__summary">
            <div className="arrange-summary__row">
              <span>Amount</span>
              <strong>{formatCurrency(submitResult.amount)}</strong>
            </div>
            <div className="arrange-summary__row">
              <span>Frequency</span>
              <strong>{frequencyLabel}</strong>
            </div>
            {submitResult.debitDay && (
              <div className="arrange-summary__row">
                <span>Debit Day</span>
                <strong>{ordinal(submitResult.debitDay)}</strong>
              </div>
            )}
            <div className="arrange-summary__row">
              <span>Status</span>
              <strong>
                {isAuthorised && <span className="status-tag status-tag--authorised">Approved</span>}
                {isRejected && <span className="status-tag status-tag--rejected">Declined</span>}
                {mandateStatus === 'canceled' && <span className="status-tag status-tag--canceled">Cancelled</span>}
                {mandateStatus === 'suspended' && <span className="status-tag status-tag--suspended">Suspended</span>}
                {isPending && <span className="status-tag status-tag--pending_approval">Pending Approval</span>}
              </strong>
            </div>
          </div>
          <button type="button" className="btn btn--primary" onClick={handleDone} data-testid="arrange-done-btn">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="section" data-testid="section-arrange">
      {/* ── Blackout Banner ── */}
      {blackout && (
        <div className="notice notice--warning arrange-blackout" data-testid="arrange-blackout">
          <p>
            <strong>DebiCheck unavailable.</strong> DebiCheck mandates cannot be processed between 8pm and 3am.
            Please try again after 3am.
          </p>
        </div>
      )}

      {/* ── Loading ── */}
      {dataLoading && (
        <div className="section--loading" data-testid="arrange-loading">
          <div className="spinner" />
          <p>Loading arrangement details...</p>
        </div>
      )}

      {/* ── Error ── */}
      {dataError && (
        <div className="notice notice--danger" data-testid="arrange-error">
          <p>{dataError}</p>
          <button type="button" className="btn btn--outline" style={{ marginTop: 12 }} onClick={retryLoad}>
            Try Again
          </button>
        </div>
      )}

      {/* ── Account blocked messages ── */}
      {arrangementData && arrangementData.hasActiveArrangement && (
        <div className="notice notice--warning" data-testid="arrange-active-warning">
          <p><strong>Active arrangement exists.</strong> {arrangementData.message}</p>
        </div>
      )}
      {arrangementData && arrangementData.isUnderDebtReview && (
        <div className="notice notice--warning" data-testid="arrange-debt-review-warning">
          <p><strong>Under debt review.</strong> {arrangementData.message}</p>
        </div>
      )}

      {/* ── Main form (only when data loaded, eligible, and not in blackout) ── */}
      {arrangementData && !dataLoading && arrangementData.isEligible && (
        <>
          {/* Status message for legal/overdue accounts */}
          {arrangementData.message && !arrangementData.hasActiveArrangement && !arrangementData.isUnderDebtReview && (
            <div className="notice notice--warning" data-testid="arrange-status-message">
              <p>{arrangementData.message}</p>
            </div>
          )}

          {/* ── Step Indicator ── */}
          <div className="arrange-steps" data-testid="arrange-steps">
            {STEPS.slice(0, 4).map((label, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isComplete = step > stepNum;
              return (
                <div
                  key={label}
                  className={`arrange-steps__step ${isActive ? 'arrange-steps__step--active' : ''} ${isComplete ? 'arrange-steps__step--complete' : ''}`}
                >
                  <div className="arrange-steps__dot">
                    {isComplete ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : stepNum}
                  </div>
                  <span className="arrange-steps__label">{label}</span>
                </div>
              );
            })}
          </div>

          {/* ── Step 1: Amount Selection ── */}
          {step === 1 && (
            <div className="arrange-step" data-testid="arrange-step-1">
              <h3 className="arrange-step__title">Select your payment amount</h3>
              <div className="arrange-amount-cards" data-testid="arrange-amount-cards">
                {(arrangementData.availableAmounts || []).map((amount) => (
                  <div
                    key={amount}
                    className={`arrange-amount-card ${selectedAmount === amount ? 'arrange-amount-card--selected' : ''} ${blackout ? 'arrange-amount-card--disabled' : ''}`}
                    onClick={() => { if (!blackout) setSelectedAmount(amount); }}
                    onKeyDown={(e) => { if (!blackout && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelectedAmount(amount); } }}
                    role="radio"
                    aria-checked={selectedAmount === amount}
                    aria-disabled={blackout}
                    tabIndex={blackout ? -1 : 0}
                    data-testid={`arrange-amount-${amount}`}
                  >
                    <div className="arrange-amount-card__value">R{amount.toLocaleString('en-ZA')}</div>
                    <div className="arrange-amount-card__label">per collection</div>
                  </div>
                ))}
              </div>
              <div className="arrange-nav">
                <div />
                <button type="button" className="btn btn--primary" disabled={!canProceedStep1 || blackout} onClick={goNext} data-testid="arrange-next-1">
                  Next
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Frequency & Pay Date ── */}
          {step === 2 && (
            <div className="arrange-step" data-testid="arrange-step-2">
              <h3 className="arrange-step__title">How often should we collect?</h3>
              <div className="arrange-frequency-cards" data-testid="arrange-frequency-cards">
                {(arrangementData.availableFrequencies || []).map((freq) => (
                  <div
                    key={freq.value}
                    className={`arrange-frequency-card ${selectedFrequency === freq.value ? 'arrange-frequency-card--selected' : ''}`}
                    onClick={() => setSelectedFrequency(freq.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFrequency(freq.value); } }}
                    role="radio"
                    aria-checked={selectedFrequency === freq.value}
                    tabIndex={0}
                    data-testid={`arrange-freq-${freq.value}`}
                  >
                    <div className="arrange-frequency-card__radio" />
                    <div className="arrange-frequency-card__label">{freq.label}</div>
                  </div>
                ))}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="arrange-pay-date">When is your next pay date?</label>
                <input
                  id="arrange-pay-date"
                  type="date"
                  className="form-input"
                  value={nextPayDate}
                  onChange={(e) => setNextPayDate(e.target.value)}
                  min={getTomorrowStr()}
                  data-testid="arrange-pay-date"
                />
              </div>

              {derivedDebitInfo && (
                <div className="arrange-debit-info" data-testid="arrange-debit-info">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>{derivedDebitInfo}</span>
                </div>
              )}

              <div className="arrange-nav">
                <button type="button" className="btn btn--outline" onClick={goBack} data-testid="arrange-back-2">
                  Back
                </button>
                <button type="button" className="btn btn--primary" disabled={!canProceedStep2} onClick={goNext} data-testid="arrange-next-2">
                  Next
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Bank Details ── */}
          {step === 3 && (
            <div className="arrange-step" data-testid="arrange-step-3">
              <h3 className="arrange-step__title">Your bank details</h3>

              <div className="form-group">
                <label className="form-label" htmlFor="arrange-bank">Bank</label>
                <select
                  id="arrange-bank"
                  className="form-select"
                  value={bankId}
                  onChange={(e) => setBankId(e.target.value)}
                  data-testid="arrange-bank-select"
                >
                  <option value="">Select your bank</option>
                  {(arrangementData.availableBanks || []).map((bank) => (
                    <option key={bank.id} value={bank.id}>{bank.bankName}</option>
                  ))}
                </select>
                {selectedBankInfo && (
                  <p className="form-hint">Branch code: {selectedBankInfo.branchCode} (auto-filled)</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="arrange-account-number">Account number</label>
                <input
                  id="arrange-account-number"
                  type="text"
                  className="form-input"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Enter your bank account number"
                  inputMode="numeric"
                  autoComplete="off"
                  data-testid="arrange-account-number"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="arrange-account-type">Account type</label>
                <select
                  id="arrange-account-type"
                  className="form-select"
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  data-testid="arrange-account-type"
                >
                  <option value="">Select account type</option>
                  {ACCOUNT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="arrange-nav">
                <button type="button" className="btn btn--outline" onClick={goBack} data-testid="arrange-back-3">
                  Back
                </button>
                <button type="button" className="btn btn--primary" disabled={!canProceedStep3} onClick={goNext} data-testid="arrange-next-3">
                  Next
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Confirm ── */}
          {step === 4 && (
            <div className="arrange-step" data-testid="arrange-step-4">
              <h3 className="arrange-step__title">Confirm your arrangement</h3>

              <div className="arrange-confirm-card" data-testid="arrange-confirm-card">
                <div className="arrange-summary__row">
                  <span>Amount</span>
                  <strong>{formatCurrency(selectedAmount)}</strong>
                </div>
                <div className="arrange-summary__row">
                  <span>Frequency</span>
                  <strong>{frequencyLabel}</strong>
                </div>
                <div className="arrange-summary__row">
                  <span>Debit Day</span>
                  <strong>{nextPayDate ? ordinal(new Date(nextPayDate).getDate()) : '-'}</strong>
                </div>
                <div className="arrange-summary__row">
                  <span>Bank</span>
                  <strong>{selectedBankInfo?.bankName || '-'}</strong>
                </div>
                <div className="arrange-summary__row">
                  <span>Account Number</span>
                  <strong>{maskAccountNumber(accountNumber)}</strong>
                </div>
                <div className="arrange-summary__row">
                  <span>Account Type</span>
                  <strong>{ACCOUNT_TYPES.find(t => t.value === accountType)?.label || accountType}</strong>
                </div>
              </div>

              <div className="arrange-consent" data-testid="arrange-consent">
                <label className="arrange-consent__label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="arrange-consent-checkbox"
                  />
                  <span>
                    I consent to a DebiCheck mandate being set up on my bank account for the above amount and frequency.
                    I understand that I will need to approve this mandate in my banking app.
                  </span>
                </label>
              </div>

              {/* Submit error */}
              {submitError && (
                <div className="notice notice--danger" data-testid="arrange-submit-error">
                  <p><strong>{submitError.message}</strong></p>
                  {submitError.errors?.length > 0 && (
                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                      {submitError.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  )}
                </div>
              )}

              <div className="arrange-nav">
                <button type="button" className="btn btn--outline" onClick={goBack} disabled={submitting} data-testid="arrange-back-4">
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleSubmit}
                  disabled={!canSubmit || blackout}
                  data-testid="arrange-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit DebiCheck Mandate'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Fallback when no data and not loading */}
      {!arrangementData && !dataLoading && !dataError && (
        <div className="section--loading">
          <div className="spinner" />
          <p>Preparing arrangement form...</p>
        </div>
      )}
    </div>
  );
}
